import React from 'react';
import { IconButton, Tooltip } from '@mui/material';
import Close from '@mui/icons-material/Close';
import Edit from '@mui/icons-material/Edit';
import LinkIcon from '@mui/icons-material/Link';
import { useI18n } from '../../../../lib/hook/useI18n';
import { SECTION_HEADER_DEEP_LINK_COPY_DISABLED_I18N_KEY } from './SectionHeaderMenuShared';

const sectionHeaderActionTooltipProps = {
  placement: 'top' as const,
  enterTouchDelay: 0,
  leaveTouchDelay: 5000,
};

export interface DeepLinkCopyIconButtonProps {
  ariaLabel: string;
  buttonClassName?: string;
  copySectionDeepLinkDisabled?: boolean;
  copySectionDeepLinkDisabledTooltip?: string;
  iconButtonSize: 'small' | 'medium' | 'large';
  onClick: () => void;
  tooltipArrow?: boolean;
}

export const DeepLinkCopyIconButton: React.FC<DeepLinkCopyIconButtonProps> = ({
  ariaLabel,
  buttonClassName,
  copySectionDeepLinkDisabled,
  copySectionDeepLinkDisabledTooltip,
  iconButtonSize,
  onClick,
  tooltipArrow,
}) => {
  const { t } = useI18n();

  const copyLinkButton = (
    <IconButton
      aria-label={ariaLabel}
      className={buttonClassName}
      disabled={copySectionDeepLinkDisabled}
      onClick={onClick}
      size={iconButtonSize}
    >
      <LinkIcon fontSize="small" />
    </IconButton>
  );

  return (
    <Tooltip
      arrow={tooltipArrow}
      title={
        copySectionDeepLinkDisabled
          ? copySectionDeepLinkDisabledTooltip ??
            t(SECTION_HEADER_DEEP_LINK_COPY_DISABLED_I18N_KEY)
          : ariaLabel
      }
      {...sectionHeaderActionTooltipProps}
    >
      {copySectionDeepLinkDisabled ? (
        <span>{copyLinkButton}</span>
      ) : (
        copyLinkButton
      )}
    </Tooltip>
  );
};

export interface EditableDeepLinkActionButtonsProps {
  buttonClassName?: string;
  copySectionDeepLinkDisabled?: boolean;
  copySectionDeepLinkDisabledTooltip?: string;
  copyLinkLabel: string;
  editLabel: string;
  iconButtonSize: 'small' | 'medium' | 'large';
  onCopyLink?: () => void;
  onEdit: () => void;
  onRemove: () => void;
  removeLabel: string;
  showRemove?: boolean;
  tooltipArrow?: boolean;
}

const EditableDeepLinkActionButtons: React.FC<
  EditableDeepLinkActionButtonsProps
> = ({
  buttonClassName,
  copySectionDeepLinkDisabled,
  copySectionDeepLinkDisabledTooltip,
  copyLinkLabel,
  editLabel,
  iconButtonSize,
  onCopyLink,
  onEdit,
  onRemove,
  removeLabel,
  showRemove = true,
  tooltipArrow,
}) => (
  <>
    <Tooltip
      arrow={tooltipArrow}
      title={editLabel}
      {...sectionHeaderActionTooltipProps}
    >
      <IconButton
        aria-label={editLabel}
        className={buttonClassName}
        onClick={onEdit}
        size={iconButtonSize}
      >
        <Edit fontSize="small" />
      </IconButton>
    </Tooltip>
    {onCopyLink && (
      <DeepLinkCopyIconButton
        ariaLabel={copyLinkLabel}
        buttonClassName={buttonClassName}
        copySectionDeepLinkDisabled={copySectionDeepLinkDisabled}
        copySectionDeepLinkDisabledTooltip={copySectionDeepLinkDisabledTooltip}
        iconButtonSize={iconButtonSize}
        onClick={onCopyLink}
        tooltipArrow={tooltipArrow}
      />
    )}
    {showRemove && (
      <Tooltip
        arrow={tooltipArrow}
        title={removeLabel}
        {...sectionHeaderActionTooltipProps}
      >
        <IconButton
          aria-label={removeLabel}
          className={buttonClassName}
          onClick={onRemove}
          size={iconButtonSize}
        >
          <Close fontSize="small" />
        </IconButton>
      </Tooltip>
    )}
  </>
);

export default EditableDeepLinkActionButtons;
